/**
 * RoomAvatar — a room's visual.
 *
 *   - SHAPE — room = CIRCLE (network = rounded square): "room vs network" at a glance.
 *   - HUE   — the room's OWN place on the whole color wheel (+ one of three tones), deterministic from
 *             (networkId, poolId) — rooms of one network no longer look alike (it was the network hue ±15°).
 *   - ICON  — a per-room glyph from the SAME curated set networks use, so the marks are one visual system.
 *
 * Same room → same avatar, always; no storage. The room tint (useRoomTint) wears this hue.
 */
import React from 'react';
import { hashString, pickIcon } from './NetworkAvatar.jsx';

// WIDE RANGE (2026-10-16, owner: "we have very few colors"): a room's hue spans the WHOLE wheel — it used to stay
// within ±15° of its network's, so every room of one network looked alike. A room still reads as a room by SHAPE
// (circle) and shares the networks' icon set; the room tint (useRoomTint) follows this hue.
// Three saturation/lightness steps multiply the distinct looks (~360 hues × 3 tones).
const TONES = [[62, 52], [70, 45], [55, 58]];

/** Hash → a well-spread 32-bit value (similar names like "room-1" / "room-2" land far apart). Pure. */
function mix(h) {
  let x = h >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  x = Math.imul(x ^ (x >>> 16), 0x45d9f3b) >>> 0;
  return (x ^ (x >>> 16)) >>> 0;
}

/** Deterministic {Icon, hue, hue2, gradient} for a room — its own place on the color wheel. */
export function roomVisual(networkId, poolId) {
  const ph = hashString(`${networkId}:${poolId}`);
  const m = mix(ph);
  const hue = m % 360;
  const hue2 = (hue + 40) % 360;
  const [sat, light] = TONES[Math.floor(m / 360) % TONES.length];
  return {
    Icon: pickIcon(ph),
    hue,
    hue2,
    gradient: `linear-gradient(135deg, hsl(${hue} ${sat}% ${light}%), hsl(${hue2} ${sat}% ${light - 6}%))`,
  };
}

export default function RoomAvatar({
  networkId,
  poolId,
  name,
  size = 22,
  className = '',
}) {
  const label = `${name || poolId || 'room'} room icon`;
  const { Icon, gradient } = roomVisual(networkId, poolId);
  const iconSize = Math.round(size * 0.55);
  return (
    <span
      role="img"
      aria-label={label}
      title={name || poolId}
      style={{ width: size, height: size, background: gradient }}
      // rounded-full = CIRCLE → the shape that distinguishes a room from a network.
      className={`inline-flex shrink-0 items-center justify-center rounded-full ${className}`}
      data-testid={`room-avatar-${networkId}-${poolId}`}
    >
      <Icon size={iconSize} strokeWidth={2} color="rgba(255,255,255,0.92)" aria-hidden />
    </span>
  );
}
